import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSecureData } from '../hooks/useSecureData';
import TherapyNoteForm from '../components/TherapyNoteForm';
import TherapyNotesList from '../components/TherapyNotesList';
import TreatmentPlanList from '../components/TreatmentPlanList';
import ClientDocuments from '../components/ClientDocuments';
import secureApiClient from '../utils/secureApiClient';
import { sortClients } from '../utils/clientSorting';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, onSnapshot } from 'firebase/firestore';

// Matches the list used at intake (ClientIntakeForm.js) for consistency.
const INSURANCE_PROVIDERS = [
  'Cigna Healthcare',
  'CenCal',
  'Beacon Health Value Options',
  'Beacon Health Strategies (GOLD COAST)',
  'Beacon Health Strategies (LA Care)',
  'Private Pay',
  'Other',
];

const formatIntakeDate = value => {
  if (!value) return 'Not recorded';
  const date = typeof value?.toDate === 'function'
    ? value.toDate()
    : typeof value?._seconds === 'number'
      ? new Date(value._seconds * 1000)
      : new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not recorded'
    : date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

const acknowledgementLabel = value => {
  if (value === true) return 'Accepted';
  if (value === false) return 'Not accepted';
  return 'Not recorded';
};

const MyClients = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, canPerform, userRole } = useSecureData();

  const [clients, setClients] = useState([]);
  const [therapistList, setTherapistList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('data');
  const [activeNotesTab, setActiveNotesTab] = useState('progress');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [sortMode, setSortMode] = useState('recent');
  const [notification, setNotification] = useState(null);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [notesRefresh, setNotesRefresh] = useState(0);
  const [assigning, setAssigning] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [expandedAssessment, setExpandedAssessment] = useState(null);
  const [clientNotes, setClientNotes] = useState([]);
  const [clientNotesLoading, setClientNotesLoading] = useState(false);
  const canManageStatus = ['admin', 'billing', 'therapist'].includes(userRole);
  const canManageAssignments = ['admin', 'billing'].includes(userRole);
  const canEditClientInfo = ['admin', 'therapist'].includes(userRole);
  const canEditBilling = userRole === 'admin';
  const canViewClinical = ['admin', 'therapist'].includes(userRole);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => setNotification({ message, type });

  // Fetch all clients from Firestore
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      setError('Authentication required');
      return;
    }
    if (!canPerform('view_clients')) {
      setLoading(false);
      setError('Insufficient permissions to view clients');
      return;
    }
    setLoading(true);
    setError(null);
    secureApiClient.makeSecureRequest(
      secureApiClient.baseURLs.cloudFunctions.getPortalClients,
      { method: 'GET' }
    )
      .then(data => {
        setClients(data.clients || []);
        setTherapistList(data.therapists || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [isAuthenticated, canPerform]);

  // Handle pre-selected client from navigation state
  useEffect(() => {
    if (location.state?.selectedClient) {
      setSelectedClient(location.state.selectedClient);
    }
  }, [location.state]);

  // Handle deep link from an email notification, e.g. /my-clients?clinicalId=xxx
  useEffect(() => {
    const targetClinicalId = searchParams.get('clinicalId');
    if (!targetClinicalId || clients.length === 0) return;
    const match = clients.find(c => c.clinicalId === targetClinicalId);
    if (match) {
      setSelectedClient(match);
      setActiveTab('data');
    }
  }, [searchParams, clients]);

  const handleStatusChange = async (clinicalId, status) => {
    if (!canManageStatus) {
      showNotification('Insufficient permissions to update client status', 'error');
      return;
    }
    // Optimistic update
    const prevClients = clients;
    setClients(prev => prev.map(c => c.clinicalId === clinicalId ? { ...c, status } : c));
    if (selectedClient?.clinicalId === clinicalId) {
      setSelectedClient(prev => ({ ...prev, status }));
    }
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.updatePortalClientStatus,
        { method: 'POST', body: JSON.stringify({ clinicalId, status }) }
      );
      const c = prevClients.find(c => c.clinicalId === clinicalId);
      const name = c ? `${c.firstName} ${c.lastName}` : 'Client';
      showNotification(`${name} status updated to ${status}`, 'success');
    } catch (err) {
      // Revert on failure
      setClients(prevClients);
      if (selectedClient?.clinicalId === clinicalId) {
        const original = prevClients.find(c => c.clinicalId === clinicalId);
        if (original) setSelectedClient(original);
      }
      showNotification('Failed to update client status', 'error');
    }
  };

  const handleAssignTherapist = async (clinicalId, therapistUid) => {
    const therapist = therapistList.find(t => t.uid === therapistUid);
    setAssigning(clinicalId);
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.assignPortalClientTherapist,
        {
          method: 'POST',
          body: JSON.stringify({ clinicalId, therapistUid, therapistName: therapist?.name || '' })
        }
      );
      const updates = { assignedTherapistId: therapistUid, assignedTherapistName: therapist?.name || '', status: 'active' };
      setClients(prev => prev.map(c => c.clinicalId === clinicalId ? { ...c, ...updates } : c));
      if (selectedClient?.clinicalId === clinicalId) {
        setSelectedClient(prev => ({ ...prev, ...updates }));
      }
      showNotification('Therapist assigned successfully', 'success');
    } catch (err) {
      showNotification('Failed to assign therapist', 'error');
    } finally {
      setAssigning(null);
    }
  };

  const handleInvite = async (client) => {
    setInviting(client.clinicalId);
    try {
      const data = await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.inviteClient,
        { method: 'POST', body: JSON.stringify({ clinicalId: client.clinicalId, email: client.email }) }
      );
      await navigator.clipboard.writeText(data.signupUrl);
      showNotification(`Invitation ready — signup link copied to clipboard for ${client.firstName}`, 'success');
    } catch (err) {
      showNotification('Failed to create invitation', 'error');
    } finally {
      setInviting(null);
    }
  };

  // Load client journal entries (real-time) inside the Therapy Notes tab.
  useEffect(() => {
    if (activeTab !== 'notes' || activeNotesTab !== 'client' || !selectedClient?.clinicalId) {
      setClientNotes([]);
      return;
    }
    setClientNotesLoading(true);
    const q = query(
      collection(db, 'clinicalRecords', selectedClient.clinicalId, 'journal'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      snap => {
        setClientNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setClientNotesLoading(false);
      },
      () => { setClientNotes([]); setClientNotesLoading(false); }
    );
    return () => unsub();
  }, [activeTab, activeNotesTab, selectedClient?.clinicalId]);

  // Assessments are part of the client's Clinical Information.
  useEffect(() => {
    if (activeTab !== 'clinical' || !selectedClient?.clinicalId) {
      setAssessments([]);
      return;
    }
    setAssessmentsLoading(true);
    getDocs(query(
      collection(db, 'clinicalRecords', selectedClient.clinicalId, 'assessments'),
      orderBy('submittedAt', 'desc')
    ))
      .then(snap => {
        setAssessments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      })
      .catch(() => setAssessments([]))
      .finally(() => setAssessmentsLoading(false));
  }, [activeTab, selectedClient?.clinicalId]);

  const handleEditStart = () => {
    const careFields = {
      preferredName:         selectedClient.preferredName || '',
      phone:                 selectedClient.phone || '',
      dateOfBirth:           selectedClient.dateOfBirth || '',
      street:                selectedClient.street || '',
      city:                  selectedClient.city || '',
      state:                 selectedClient.state || '',
      zip:                   selectedClient.zip || '',
      emergencyContactName:  selectedClient.emergencyContactName || '',
      emergencyContactPhone: selectedClient.emergencyContactPhone || '',
      gender:                selectedClient.gender || '',
      maritalStatus:         selectedClient.maritalStatus || '',
      employmentStatus:      selectedClient.employmentStatus || '',
      referralSource:        selectedClient.referralSource || '',
      reasonForReachingOut:  selectedClient.reasonForReachingOut || '',
      // Clinical Information
      medicationsCurrent:    selectedClient.medicationsCurrent === true,
      medicationsList:       selectedClient.medicationsList || '',
    };
    setEditForm(canEditBilling ? {
      ...careFields,
      paymentPreference: selectedClient.paymentPreference || '',
      insuranceProvider: selectedClient.insuranceProvider || '',
      insurancePlanName: selectedClient.insurancePlanName || '',
      insuranceMemberId: selectedClient.insuranceMemberId || '',
      insuranceGroupId: selectedClient.insuranceGroupId || '',
      copay: selectedClient.copay || '',
      deductible: selectedClient.deductible || '',
    } : careFields);
    setEditMode(true);
  };

  const handleEditCancel = () => { setEditMode(false); setEditForm({}); };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      const result = await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.submitClientIntake,
        { method: 'POST', body: JSON.stringify({ ...editForm, targetUid: selectedClient.uid }) }
      );
      const updated = { ...selectedClient, ...editForm, intakeCompleted: result.intakeCompleted, intakeSubmittedAt: result.intakeSubmittedAt };
      setClients(prev => prev.map(c => c.uid === selectedClient.uid ? updated : c));
      setSelectedClient(updated);
      setEditMode(false);
      setEditForm({});
      showNotification('Client information updated successfully');
    } catch (err) {
      showNotification(err.message || 'Failed to save changes', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const filteredClients = sortClients(clients.filter(c => {
    const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase());
    const isActive = c.status !== 'inactive';
    return matchesSearch && (showActiveOnly ? isActive : !isActive);
  }), sortMode);

  if (loading) return <div className="flex"><div className="flex-1 p-8">Loading clients…</div></div>;
  if (error) return <div className="flex"><div className="flex-1 p-8 text-red-600">Error: {error}</div></div>;

  return (
    <div className="flex">
      <div className="flex-1 p-8">

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 z-50 max-w-sm">
            <div className={`rounded-lg shadow-lg p-4 ${
              notification.type === 'success' ? 'bg-green-500 text-white' :
              notification.type === 'error'   ? 'bg-red-500 text-white' :
              'bg-blue-500 text-white'
            }`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{notification.message}</p>
                <button onClick={() => setNotification(null)} className="ml-4 text-white hover:text-gray-200">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="container mx-auto">
          {/* Header */}
          <div className="flex flex-col gap-4 mb-6 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h1 className="text-2xl font-bold">My Clients</h1>
              <p className="text-gray-600 mt-1">
                {filteredClients.length} {showActiveOnly ? 'active / pending' : 'inactive'} clients
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Search clients…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full sm:w-64 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                Sort:
                <select
                  value={sortMode}
                  onChange={e => setSortMode(e.target.value)}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  aria-label="Sort clients"
                >
                  <option value="recent">Recently added</option>
                  <option value="alphabetical">A–Z by last name</option>
                </select>
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Show:</span>
                <button
                  onClick={() => setShowActiveOnly(true)}
                  className={`px-3 py-1 rounded text-sm font-medium ${showActiveOnly ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >Active / Pending</button>
                <button
                  onClick={() => setShowActiveOnly(false)}
                  className={`px-3 py-1 rounded text-sm font-medium ${!showActiveOnly ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >Inactive</button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Client List */}
            <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-semibold mb-4">
                Client List
                <span className="text-sm font-normal text-gray-500 ml-2">({filteredClients.length})</span>
              </h2>
              <div className="space-y-2">
                {filteredClients.length === 0 ? (
                  <div className="text-gray-500 text-center py-4 text-sm">
                    {clients.length === 0
                      ? 'No clients found'
                      : `No ${showActiveOnly ? 'active / pending' : 'inactive'} clients`}
                  </div>
                ) : (
                  filteredClients.map(c => (
                    <div
                      key={c.uid}
                      className={`p-3 rounded cursor-pointer ${selectedClient?.uid === c.uid ? 'bg-brand-100' : 'hover:bg-gray-100'}`}
                      onClick={() => { setSelectedClient(c); setActiveTab('data'); setShowNoteForm(false); setAssessments([]); setClientNotes([]); setEditMode(false); setEditForm({}); }}
                    >
                      <div className="font-medium">{c.firstName} {c.lastName}</div>
                      <div className="text-sm text-gray-500">
                        {c.insuranceProvider || c.insuranceType || 'No insurance on file'}
                      </div>
                      <div className={`text-sm mt-0.5 ${c.status === 'active' ? 'text-green-600' : 'text-amber-600'}`}>
                        {c.status === 'active' ? 'Active' : c.status === 'pending' ? 'Pending' : 'Inactive'}
                      </div>
                      <div className={`text-xs font-medium mt-1 ${c.intakeCompleted ? 'text-green-700' : 'text-amber-700'}`}>
                        {c.intakeCompleted ? 'Intake complete' : 'Intake incomplete'}
                      </div>
                      {!c.assignedTherapistId && (
                        <div className="text-xs text-amber-600 font-medium mt-0.5">⚠ Unassigned</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Client Detail Panel */}
            {selectedClient && (
              <div className="lg:col-span-3 bg-white rounded-lg shadow p-4">
                {/* Detail Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {selectedClient.firstName} {selectedClient.lastName}
                      {selectedClient.preferredName && selectedClient.preferredName !== selectedClient.firstName && (
                        <span className="text-base font-normal text-gray-500 ml-2">
                          ({selectedClient.preferredName})
                        </span>
                      )}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        selectedClient.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : selectedClient.status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {selectedClient.status === 'active' ? 'Active' : selectedClient.status === 'pending' ? 'Pending' : 'Inactive'}
                      </span>
                      {selectedClient.assignedTherapistName && (
                        <span className="text-xs text-gray-500">
                          Therapist: {selectedClient.assignedTherapistName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap justify-end">
                    {/* Status change */}
                    {canManageStatus && (
                      <select
                        value={selectedClient.status === 'active' ? 'active' : 'inactive'}
                        onChange={e => handleStatusChange(selectedClient.clinicalId, e.target.value)}
                        className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    )}

                    {/* Therapist assignment — admin/billing only */}
                    {canManageAssignments && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600 font-medium">Assign:</label>
                        <select
                          value={selectedClient.assignedTherapistId || ''}
                          disabled={assigning === selectedClient.clinicalId}
                          onChange={e => {
                            const uid = e.target.value;
                            if (!uid) return;
                            handleAssignTherapist(selectedClient.clinicalId, uid);
                          }}
                          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2"
                          style={{ '--tw-ring-color': '#1e562d' }}
                        >
                          <option value="">— Select therapist —</option>
                          {therapistList.map(t => (
                            <option key={t.uid} value={t.uid}>{t.name}</option>
                          ))}
                        </select>
                        {assigning === selectedClient.clinicalId && (
                          <span className="text-xs text-gray-400">Saving…</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Invite to Portal — only for migrated clients without a verified account */}
                  {selectedClient.migratedFromSheet && !selectedClient.emailVerified && canManageAssignments && (
                    <button
                      onClick={() => handleInvite(selectedClient)}
                      disabled={inviting === selectedClient.clinicalId}
                      className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-amber-400 whitespace-nowrap"
                      title="Create a signup link for this client and copy it to the clipboard"
                    >
                      {inviting === selectedClient.clinicalId ? 'Creating invite…' : 'Invite to portal'}
                    </button>
                  )}
                </div>

                {!selectedClient.intakeCompleted && (
                  <div role="status" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    Intake incomplete. Required questionnaire answers or consents are missing.
                    Saved answers are available below; ask the client to finish Client Intake in their portal.
                  </div>
                )}

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    {[
                      { key: 'data',         label: 'Client Information' },
                      { key: 'clinical',     label: 'Clinical Information' },
                      { key: 'notes',        label: 'Therapy Notes' },
                      { key: 'insurance',    label: 'Billing Info' },
                    ].filter(tab => {
                      if (tab.key === 'insurance') return ['admin', 'billing', 'therapist'].includes(userRole);
                      if (['clinical', 'notes'].includes(tab.key)) return canViewClinical;
                      return true;
                    }).map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                          activeTab === tab.key
                            ? 'border-brand-500 text-brand-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="space-y-6">

                  {/* ── Client Information ── */}
                  {activeTab === 'data' && (
                    <section>

                      {/* Edit / Save / Cancel — admin only */}
                      {canEditClientInfo && (
                        <div className="flex justify-end mb-4">
                          {editMode ? (
                            <div className="flex gap-2">
                              <button
                                onClick={handleEditCancel}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleEditSave}
                                disabled={editSaving}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-60"
                                style={{ backgroundColor: '#1e562d' }}
                              >
                                {editSaving ? 'Saving…' : 'Save Changes'}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={handleEditStart}
                              className="px-4 py-1.5 rounded-lg text-sm font-medium text-white"
                              style={{ backgroundColor: '#1e562d' }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      )}

                      <h3 className="text-lg font-medium mb-2">Basic Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Legal First Name</label>
                          <div className="mt-0.5">{selectedClient.firstName || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Legal Last Name</label>
                          <div className="mt-0.5">{selectedClient.lastName || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Preferred Name</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.preferredName} onChange={e => setEditForm(f => ({ ...f, preferredName: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.preferredName || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Date of Birth</label>
                          {editMode ? (
                            <input type="date" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.dateOfBirth} onChange={e => setEditForm(f => ({ ...f, dateOfBirth: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.dateOfBirth || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Email</label>
                          <div className="mt-0.5">{selectedClient.email || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Phone</label>
                          {editMode ? (
                            <input type="tel" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.phone || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Gender / Pronouns</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.gender} onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}>
                              <option value="">Select…</option>
                              {['He/Him','She/Her','They/Them','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.gender || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Marital Status</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.maritalStatus} onChange={e => setEditForm(f => ({ ...f, maritalStatus: e.target.value }))}>
                              <option value="">Select…</option>
                              {['Married','Single','Divorced','Separated','Widowed','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.maritalStatus || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Employment Status</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.employmentStatus} onChange={e => setEditForm(f => ({ ...f, employmentStatus: e.target.value }))}>
                              <option value="">Select…</option>
                              {['Full Time','Part Time','Unemployed','Self-Employed','Attending School','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.employmentStatus || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Referral Source</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.referralSource} onChange={e => setEditForm(f => ({ ...f, referralSource: e.target.value }))}>
                              <option value="">Select…</option>
                              {['Insurance Website','Psychology Today','Personal Referral','shanebruce.com','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.referralSource || '—'}</div>
                          )}
                        </div>
                      </div>

                      <h3 className="text-lg font-medium mb-2 mt-6">Address</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <label className="text-sm text-gray-500">Street</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.street} onChange={e => setEditForm(f => ({ ...f, street: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.street || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">City</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.city || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">State</label>
                          {editMode ? (
                            <input maxLength={2} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.state} onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.state || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Zip</label>
                          {editMode ? (
                            <input maxLength={10} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.zip} onChange={e => setEditForm(f => ({ ...f, zip: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.zip || '—'}</div>
                          )}
                        </div>
                      </div>

                      <h3 className="text-lg font-medium mb-2 mt-6">Emergency Contact</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Name</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.emergencyContactName} onChange={e => setEditForm(f => ({ ...f, emergencyContactName: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.emergencyContactName || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Phone</label>
                          {editMode ? (
                            <input type="tel" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.emergencyContactPhone} onChange={e => setEditForm(f => ({ ...f, emergencyContactPhone: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.emergencyContactPhone || '—'}</div>
                          )}
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── Insurance & Billing ── */}
                  {activeTab === 'insurance' && (
                    <section>
                      {/* Edit / Save / Cancel — admin only */}
                      {canEditBilling && (
                        <div className="flex justify-end mb-4">
                          {editMode ? (
                            <div className="flex gap-2">
                              <button
                                onClick={handleEditCancel}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleEditSave}
                                disabled={editSaving}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-60"
                                style={{ backgroundColor: '#1e562d' }}
                              >
                                {editSaving ? 'Saving…' : 'Save Changes'}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={handleEditStart}
                              className="px-4 py-1.5 rounded-lg text-sm font-medium text-white"
                              style={{ backgroundColor: '#1e562d' }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      )}

                      <h3 className="text-lg font-medium mb-2">Payment</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Payment Preference</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.paymentPreference} onChange={e => setEditForm(f => ({ ...f, paymentPreference: e.target.value }))}>
                              <option value="">Select…</option>
                              <option value="insurance">Insurance Benefits</option>
                              <option value="private_pay">Private Pay</option>
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.paymentPreference || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Copay</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" placeholder="$0" value={editForm.copay} onChange={e => setEditForm(f => ({ ...f, copay: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.copay || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Deductible</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" placeholder="$0" value={editForm.deductible} onChange={e => setEditForm(f => ({ ...f, deductible: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.deductible || '—'}</div>
                          )}
                        </div>
                      </div>

                      <h3 className="text-lg font-medium mb-2 mt-6">Insurance</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Provider</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.insuranceProvider} onChange={e => setEditForm(f => ({ ...f, insuranceProvider: e.target.value }))}>
                              <option value="">Select…</option>
                              {INSURANCE_PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.insuranceProvider || selectedClient.insuranceType || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Plan Name</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.insurancePlanName} onChange={e => setEditForm(f => ({ ...f, insurancePlanName: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.insurancePlanName || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Member ID</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.insuranceMemberId} onChange={e => setEditForm(f => ({ ...f, insuranceMemberId: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.insuranceMemberId || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Group ID</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.insuranceGroupId} onChange={e => setEditForm(f => ({ ...f, insuranceGroupId: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.insuranceGroupId || '—'}</div>
                          )}
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── Clinical Information ── */}
                  {activeTab === 'clinical' && (
                    <section>
                      {/* Edit / Save / Cancel — admin only */}
                      {canEditClientInfo && (
                        <div className="flex justify-end mb-4">
                          {editMode ? (
                            <div className="flex gap-2">
                              <button
                                onClick={handleEditCancel}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleEditSave}
                                disabled={editSaving}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-60"
                                style={{ backgroundColor: '#1e562d' }}
                              >
                                {editSaving ? 'Saving…' : 'Save Changes'}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={handleEditStart}
                              className="px-4 py-1.5 rounded-lg text-sm font-medium text-white"
                              style={{ backgroundColor: '#1e562d' }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      )}

                      <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 sm:p-5">
                        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <h3 className="text-lg font-medium">Clinical Intake</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Client-provided information from the intake process.
                            </p>
                          </div>
                          <div className="sm:text-right">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                              selectedClient.intakeCompleted
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {selectedClient.intakeCompleted ? 'Completed' : 'Intake incomplete'}
                            </span>
                            {selectedClient.intakeSubmittedAt && (
                              <div className="mt-1 text-xs text-gray-500">
                                Submitted {formatIntakeDate(selectedClient.intakeSubmittedAt)}
                              </div>
                            )}
                          </div>
                        </div>

                        <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600">
                          Background
                        </h4>
                        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                          <div>
                            <div className="text-gray-500">Employer / School</div>
                            <div className="mt-0.5">{selectedClient.employerOrSchool || 'Not provided'}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Marital Status</div>
                            <div className="mt-0.5">{selectedClient.maritalStatus || 'Not provided'}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Gender / Pronouns</div>
                            <div className="mt-0.5">{selectedClient.gender || 'Not provided'}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Employment Status</div>
                            <div className="mt-0.5">{selectedClient.employmentStatus || 'Not provided'}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Referral Source</div>
                            <div className="mt-0.5">{selectedClient.referralSource || 'Not provided'}</div>
                          </div>
                        </div>

                        <div className="mt-6">
                          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                            Reason for Reaching Out
                          </h4>
                          {editMode ? (
                            <textarea
                              rows={4}
                              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none"
                              value={editForm.reasonForReachingOut}
                              onChange={e => setEditForm(f => ({ ...f, reasonForReachingOut: e.target.value }))}
                            />
                          ) : (
                            <div className="whitespace-pre-wrap rounded-lg border-l-4 border-blue-400 bg-blue-50 p-4 text-sm">
                              {selectedClient.reasonForReachingOut || 'Not provided'}
                            </div>
                          )}
                        </div>

                        <div className="mt-6">
                          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                            Current Medications
                          </h4>
                          {editMode ? (
                            <div className="space-y-2">
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={editForm.medicationsCurrent}
                                  onChange={e => setEditForm(f => ({ ...f, medicationsCurrent: e.target.checked }))}
                                />
                                Currently taking medication
                              </label>
                              {editForm.medicationsCurrent && (
                                <input
                                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                  placeholder="List medications"
                                  value={editForm.medicationsList}
                                  onChange={e => setEditForm(f => ({ ...f, medicationsList: e.target.value }))}
                                />
                              )}
                            </div>
                          ) : (
                            <div className="rounded-lg bg-white p-3 text-sm">
                              {selectedClient.medicationsCurrent === true
                                ? selectedClient.medicationsList || 'Yes — no medication details provided'
                                : selectedClient.medicationsCurrent === false
                                  ? 'No'
                                  : 'Not provided'}
                            </div>
                          )}
                        </div>

                        {selectedClient.conditions?.length > 0 && (
                          <div className="mt-6">
                            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                              Reported Concerns
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {selectedClient.conditions.map((condition, index) => (
                                <span key={index} className="inline-block rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-700">
                                  {condition}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <h4 className="mb-3 mt-6 text-sm font-semibold uppercase tracking-wide text-gray-600">
                          Insurance Details Provided at Intake
                        </h4>
                        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                          <div>
                            <div className="text-gray-500">Insurance Category</div>
                            <div className="mt-0.5">{selectedClient.insuranceType || 'Not provided'}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Provider</div>
                            <div className="mt-0.5">{selectedClient.insuranceProvider || 'Not provided'}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Insurance Phone</div>
                            <div className="mt-0.5">{selectedClient.insurancePhone || 'Not provided'}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Out-of-Pocket Maximum</div>
                            <div className="mt-0.5">{selectedClient.oopMax || 'Not provided'}</div>
                          </div>
                        </div>

                        <h4 className="mb-3 mt-6 text-sm font-semibold uppercase tracking-wide text-gray-600">
                          Consent Acknowledgements
                        </h4>
                        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                          <div>
                            <div className="text-gray-500">Informed Consent</div>
                            <div className="mt-0.5">{acknowledgementLabel(selectedClient.consentInformedConsent)}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Cancellation Policy</div>
                            <div className="mt-0.5">{acknowledgementLabel(selectedClient.consentCancellationPolicy)}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Telehealth Consent</div>
                            <div className="mt-0.5">{acknowledgementLabel(selectedClient.consentTelehealth)}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">Acknowledged</div>
                            <div className="mt-0.5">{formatIntakeDate(selectedClient.intakeSubmittedAt)}</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-8 pt-6 border-t">
                        <ClientDocuments clinicalId={selectedClient.clinicalId} />
                      </div>
                    </section>
                  )}

                  {/* ── Therapy Notes ── */}
                  {activeTab === 'notes' && (
                    <section>
                      {/* Client summary banner */}
                      <div className="mb-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                        <h4 className="text-sm font-semibold text-blue-800 mb-2">Client Summary</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-blue-600 font-medium">Date of Birth:</span>
                            <div className="text-gray-800">{selectedClient.dateOfBirth || 'Not on file'}</div>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Phone:</span>
                            <div className="text-gray-800">{selectedClient.phone || 'Not on file'}</div>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Emergency Contact:</span>
                            <div className="text-gray-800">{selectedClient.emergencyContactName || 'Not provided'}</div>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Assigned Therapist:</span>
                            <div className="text-gray-800">{selectedClient.assignedTherapistName || 'Unassigned'}</div>
                          </div>
                        </div>
                      </div>

                      {/* Note template link */}
                      <div className="mb-4">
                        <a
                          href="https://integritymhc.com/template-notes/"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-800 hover:underline"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Note Template
                        </a>
                      </div>

                      {/* Notes sub-tabs */}
                      <div className="border-b border-gray-200 mb-6">
                        <nav className="-mb-px flex space-x-8">
                          <button
                            onClick={() => setActiveNotesTab('progress')}
                            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                              activeNotesTab === 'progress'
                                ? 'border-brand-500 text-brand-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Progress Notes
                          </button>
                          <button
                            onClick={() => setActiveNotesTab('treatment')}
                            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                              activeNotesTab === 'treatment'
                                ? 'border-green-500 text-green-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Treatment Plan
                          </button>
                          <button
                            onClick={() => setActiveNotesTab('client')}
                            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                              activeNotesTab === 'client'
                                ? 'border-brand-500 text-brand-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Client Notes
                          </button>
                        </nav>
                      </div>

                      {/* Progress Notes */}
                      {activeNotesTab === 'progress' && (
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-md font-medium">Progress Notes</h4>
                            {canPerform('create_notes') && (
                              <button
                                onClick={() => setShowNoteForm(!showNoteForm)}
                                className={`px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                                  showNoteForm
                                    ? 'bg-gray-500 text-white hover:bg-gray-600'
                                    : 'bg-brand-600 text-white hover:bg-brand-700'
                                }`}
                              >
                                {showNoteForm ? 'Cancel' : 'New Progress Note'}
                              </button>
                            )}
                          </div>

                          {showNoteForm && canPerform('create_notes') && (
                            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                              <TherapyNoteForm
                                clinicalId={selectedClient.clinicalId}
                                clientName={`${selectedClient.firstName} ${selectedClient.lastName}`}
                                clientData={selectedClient}
                                onClose={() => setShowNoteForm(false)}
                                onSaved={() => {
                                  setShowNoteForm(false);
                                  setNotesRefresh(prev => prev + 1);
                                  showNotification('Progress note saved successfully');
                                }}
                              />
                            </div>
                          )}

                          {canPerform('view_notes') ? (
                            <TherapyNotesList
                              clinicalId={selectedClient.clinicalId}
                              clientName={`${selectedClient.firstName} ${selectedClient.lastName}`}
                              key={notesRefresh}
                            />
                          ) : (
                            <div className="text-gray-500 text-center py-8 text-sm">
                              You don't have permission to view progress notes
                            </div>
                          )}
                        </div>
                      )}

                      {/* Treatment Plans */}
                      {activeNotesTab === 'treatment' && (
                        <div>
                          {canPerform('view_notes') ? (
                            <TreatmentPlanList
                              clinicalId={selectedClient.clinicalId}
                              clientName={`${selectedClient.firstName} ${selectedClient.lastName}`}
                              clientData={selectedClient}
                            />
                          ) : (
                            <div className="text-gray-500 text-center py-8 text-sm">
                              You don't have permission to view treatment plans
                            </div>
                          )}
                        </div>
                      )}
                    </section>
                  )}

                  {/* ── Client Notes (journal), within Therapy Notes ── */}
                  {activeTab === 'notes' && activeNotesTab === 'client' && (
                    <section className="mt-2">
                      <h3 className="text-lg font-medium mb-4">Client Notes</h3>
                      {clientNotesLoading ? (
                        <div className="text-gray-400 text-sm text-center py-8">Loading client notes…</div>
                      ) : clientNotes.length === 0 ? (
                        <div className="text-center py-10">
                          <div className="text-gray-500 text-sm font-medium mb-1">No journal entries yet</div>
                          <p className="text-xs text-gray-400">
                            This client hasn't written any notes. They can add entries from their portal under "My Journal".
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <p className="text-xs text-gray-400 mb-2">
                            {clientNotes.length} {clientNotes.length === 1 ? 'entry' : 'entries'} — read only
                          </p>
                          {clientNotes.map(entry => {
                            const date = entry.createdAt?.toDate?.() || new Date(entry.createdAt || 0);
                            return (
                              <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                                <div className="text-xs text-gray-400 mb-2">
                                  {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                  {' · '}
                                  {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </div>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{entry.content}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  )}

                  {/* ── Assessments, within Clinical Information ── */}
                  {activeTab === 'clinical' && (
                    <section className="mt-8 border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-medium mb-4">Client Assessments</h3>
                      {assessmentsLoading ? (
                        <div className="text-center text-gray-500 py-8">Loading assessments…</div>
                      ) : assessments.length === 0 ? (
                        <div className="text-center text-gray-500 py-8 text-sm">No assessments on file</div>
                      ) : (
                        <div className="space-y-4">
                          {assessments.map(a => {
                            const date = a.submittedAt?.toDate
                              ? a.submittedAt.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : a.submittedAt
                              ? new Date(a.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : 'Unknown date';
                            const isExpanded = expandedAssessment === a.id;
                            const checkboxes = a.selectedCheckboxes || {};
                            const checkedItems = Object.values(checkboxes)
                              .flatMap(group => Object.values(group || {}))
                              .flatMap(arr => Array.isArray(arr) ? arr : []);
                            const analysisStatus = a.analysisStatus || (a.aiAnalysis ? 'completed' : 'pending');
                            const analysisStatusLabel = analysisStatus === 'completed'
                              ? 'AI draft available'
                              : analysisStatus === 'failed'
                                ? 'AI analysis unavailable'
                                : 'AI analysis pending';
                            const analysisStatusClass = analysisStatus === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : analysisStatus === 'failed'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-700';

                            return (
                              <div key={a.id} className="border rounded-lg overflow-hidden">
                                <div
                                  className="bg-gray-50 p-4 flex justify-between items-center cursor-pointer hover:bg-gray-100"
                                  onClick={() => setExpandedAssessment(isExpanded ? null : a.id)}
                                >
                                  <div>
                                    <div className="font-medium text-gray-800">Assessment — {date}</div>
                                    <div className="text-sm text-gray-500 mt-0.5">
                                      {checkedItems.length > 0
                                        ? `${checkedItems.length} reported symptom${checkedItems.length !== 1 ? 's' : ''}`
                                        : 'No symptoms selected'}
                                      <span className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${analysisStatusClass}`}>
                                        {analysisStatusLabel}
                                      </span>
                                    </div>
                                  </div>
                                  <svg
                                    className={`w-5 h-5 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>

                                {isExpanded && (
                                  <div className="p-4 border-t space-y-5">
                                    {/* Basic info */}
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      {a.age && <div><span className="text-gray-500">Age:</span> <span>{a.age}</span></div>}
                                      {a.maritalStatus && <div><span className="text-gray-500">Marital Status:</span> <span>{a.maritalStatus}</span></div>}
                                      {a.children && <div><span className="text-gray-500">Children:</span> <span>{a.children}</span></div>}
                                      {a.medications && <div><span className="text-gray-500">Current Medications:</span> <span>{a.medications}</span></div>}
                                      {a.previousDiagnosis && <div className="col-span-2"><span className="text-gray-500">Previous Diagnosis:</span> <span>{a.previousDiagnosis}</span></div>}
                                      {a.medicalCondition && <div className="col-span-2"><span className="text-gray-500">Medical Conditions:</span> <span>{a.medicalCondition}</span></div>}
                                    </div>

                                    {/* Reported symptoms */}
                                    {checkedItems.length > 0 && (
                                      <div>
                                        <h5 className="text-sm font-semibold text-gray-700 mb-2">Reported Symptoms</h5>
                                        <div className="flex flex-wrap gap-2">
                                          {checkedItems.map((item, index) => (
                                            <span key={`${index}-${item}`} className="bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                                              {item}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Additional info */}
                                    {a.additionalInfo && (
                                      <div>
                                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Additional Information</h5>
                                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded whitespace-pre-wrap">{a.additionalInfo}</p>
                                      </div>
                                    )}

                                    {/* AI-generated draft — never replaces therapist review */}
                                    {analysisStatus === 'completed' && a.aiAnalysis && (
                                      <div className="border-t pt-4">
                                        <h5 className="text-sm font-semibold text-brand-700 mb-1">AI-Generated Draft</h5>
                                        <p className="mb-3 text-xs text-gray-500">
                                          For therapist review only. This is not a diagnosis or finalized treatment plan.
                                        </p>
                                        {a.aiAnalysis.summary && a.aiAnalysis.summary !== 'Analysis unavailable' && (
                                          <div className="mb-4">
                                            <h6 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Clinical Summary</h6>
                                            <p className="text-sm text-gray-700 bg-brand-50 p-3 rounded whitespace-pre-wrap">{a.aiAnalysis.summary}</p>
                                          </div>
                                        )}
                                        {a.aiAnalysis.suggestedPlan && a.aiAnalysis.suggestedPlan !== 'Analysis unavailable' && (
                                          <div>
                                            <h6 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Suggested Treatment Plan</h6>
                                            <p className="text-sm text-gray-700 bg-green-50 p-3 rounded whitespace-pre-wrap">{a.aiAnalysis.suggestedPlan}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {analysisStatus === 'pending' && (
                                      <div className="border-t pt-4 text-sm text-amber-700">
                                        The client’s answers are available. AI analysis is still processing.
                                      </div>
                                    )}
                                    {analysisStatus === 'failed' && (
                                      <div className="border-t pt-4 text-sm text-red-700">
                                        The client’s answers are available, but AI analysis could not be completed.
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  )}

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyClients;
